import { Client, WorkSite, LocationDetails } from '@/types';

export const UNIQUE_ADDRESS_NAME = 'Dirección única';

/**
 * Normaliza una cadena de texto eliminando acentos y espacios adicionales para comparaciones seguras.
 */
export const normalizeText = (text?: string | null): string => {
    if (!text) return '';
    return text
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
};

/**
 * Detecta si una obra es de tipo "Dirección única" / "Domicilio particular".
 * Prioriza el flag booleano `isUniqueAddress` y mantiene fallback por nombre para datos legacy en memoria.
 */
export const isUniqueAddressSite = (site?: WorkSite | null): boolean => {
    if (!site) return false;
    if (site.isUniqueAddress === true) return true;
    const normalized = normalizeText(site.name);
    return normalized === 'direccion unica' || normalized === 'domicilio particular';
};

/**
 * Obtiene la obra de tipo dirección única de un cliente o lista de obras si existe.
 */
export const getUniqueAddressSite = (clientOrSites?: Client | WorkSite[] | null): WorkSite | undefined => {
    if (!clientOrSites) return undefined;
    const sites = Array.isArray(clientOrSites) ? clientOrSites : clientOrSites.workSites;
    if (!sites || sites.length === 0) return undefined;
    return sites.find(isUniqueAddressSite);
};

/**
 * Comprueba si un cliente o lista de obras tiene configurada una dirección única.
 */
export const clientHasUniqueAddress = (clientOrSites?: Client | WorkSite[] | null): boolean => {
    return Boolean(getUniqueAddressSite(clientOrSites));
};

/**
 * Detecta si un título o nombre de obra corresponde a una dirección única / domicilio particular genérico.
 * Devuelve true únicamente para cadenas vacías o equivalentes a 'dirección única' / 'domicilio particular'.
 */
export const isGenericSiteTitle = (title?: string | null): boolean => {
    if (!title) return true;
    const normalized = normalizeText(title);
    return normalized === '' || normalized === 'direccion unica' || normalized === 'domicilio particular';
};

/**
 * Retorna la etiqueta formal legible para direcciones únicas según el contexto o dato histórico.
 * 'domicilio particular' -> 'Domicilio'
 * Cualquier otro genérico -> 'Sede Principal'
 */
export const getUniqueAddressDisplayLabel = (titleOrName?: string | null): string => {
    const normalized = normalizeText(titleOrName);
    if (normalized === 'domicilio particular') {
        return 'Domicilio';
    }
    return 'Sede Principal';
};

/**
 * Capitaliza suavemente solo el primer carácter de la cadena,
 * sin forzar minúsculas en el resto (preserva intactas siglas como "LED", "C.P.", "Nº").
 */
export const capitalizeFirst = (text?: string | null): string => {
    if (!text) return '';
    const trimmed = text.trim();
    if (!trimmed) return '';
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

/**
 * Extrae la primera línea de una descripción limpiando prefijos de continuación y truncando con elipsis.
 * NOTA DE MANTENIMIENTO: La regex de prefijos de continuación ("Continuación de:", "[temp-...]")
 * está deliberadamente duplicada aquí para evitar dependencias circulares con orderChainUtils.ts (getBaseDescription).
 * Si se modifica esta expresión, mantener sincronizado con orderChainUtils.ts.
 */
export const extractCleanFirstLine = (text?: string | null, maxLength: number = 70): string => {
    if (!text) return '';
    const withoutPrefix = text
        .trim()
        .replace(/^continuaci[oó]n\s+de(\s+jornada)?(:)?\s*/i, '')
        .replace(/\[temp-[^\]]*\]/i, '')
        .trim();
    const firstLine = withoutPrefix.split('\n')[0].trim();
    if (!firstLine) return '';
    const capitalized = capitalizeFirst(firstLine);
    if (capitalized.length <= maxLength) return capitalized;
    return capitalized.slice(0, maxLength).trimEnd() + '…';
};

export interface OrderDisplayTitleOptions {
    prefer?: 'description' | 'client';
    maxLength?: number;
}

/**
 * Obtiene el título display contextual para un parte de trabajo.
 * 
 * Regla:
 * 1. Si el título es específico y no es genérico ni idéntico al cliente (comparado con normalizeText),
 *    es el título real asignado al trabajo -> se respeta.
 * 2. Si la obra es específica (no genérica, ej. "Edificio Girasol") -> se respeta.
 * 3. Si es dirección única / genérico:
 *    - prefer === 'description': descripción limpia (hasta maxLength) -> clientName -> 'Sin título'.
 *    - prefer === 'client': clientName -> descripción limpia -> 'Sin título'.
 */
export const getOrderDisplayTitle = (
    order: {
        title?: string | null;
        obra?: string | null;
        description?: string | null;
        clientName?: string | null;
        partNumber?: number | null;
    },
    options?: OrderDisplayTitleOptions
): string => {
    const prefer = options?.prefer || 'description';
    const maxLength = options?.maxLength || 70;

    const normTitle = normalizeText(order.title);
    const normClient = normalizeText(order.clientName);
    const isGenericTitle = isGenericSiteTitle(order.title);
    const isGenericObra = isGenericSiteTitle(order.obra);
    const isTitleClientDuplicate = Boolean(normClient && normTitle === normClient);

    // 1. Título real explícito (no genérico y no duplicado automático del nombre del cliente)
    if (order.title && !isGenericTitle && !isTitleClientDuplicate) {
        return order.title;
    }

    // 2. Obra real física (no genérica, ej. "Edificio Girasol", "Hospital Norte")
    if (order.obra && !isGenericObra) {
        return order.obra;
    }

    // 3. Caso genérico / dirección única: resolver por preferencia contextual
    const cleanDesc = extractCleanFirstLine(order.description, maxLength);
    const client = order.clientName?.trim();

    if (prefer === 'description') {
        if (cleanDesc) return cleanDesc;
        if (client) return client;
    } else {
        if (client) return client;
        if (cleanDesc) return cleanDesc;
    }

    return 'Sin título';
};

/**
 * Parser "best-effort" en frontend para direcciones legacy no estructuradas.
 * Solo asiste precargando el formulario; jamás guarda datos sin confirmación del usuario.
 *
 * Formatos habituales en España:
 * - "Calle Bahía, 20, 18690, Almuñécar, Granada"
 * - "Gran Vía, 12 (3ºB), 28013 Madrid, Madrid"
 * - "Ellis Street, 94108, San Francisco, California"
 */
export const parseLegacyAddress = (rawAddress?: string): Partial<LocationDetails> => {
    if (!rawAddress || typeof rawAddress !== 'string') return {};
    const clean = rawAddress.trim();
    if (!clean) return {};

    const result: Partial<LocationDetails> = {
        street: '',
        number: '',
        extra: '',
        zipCode: '',
        city: '',
        province: ''
    };

    // Extraer complemento tipo (3ºB) si viene entre paréntesis
    const extraMatch = clean.match(/\(([^)]+)\)/);
    if (extraMatch) {
        result.extra = extraMatch[1].trim();
    }
    const cleanWithoutExtra = clean.replace(/\([^)]+\)/, '').trim();

    // Separar por comas
    const parts = cleanWithoutExtra.split(',').map(p => p.trim()).filter(Boolean);

    if (parts.length === 1) {
        // Solo una cadena: colocar en calle
        result.street = parts[0];
        return result;
    }

    // Heurística de código postal (5 dígitos consecutivos en España)
    let foundZipIndex = -1;
    for (let i = 0; i < parts.length; i++) {
        const zipMatch = parts[i].match(/\b(\d{5})\b/);
        if (zipMatch) {
            result.zipCode = zipMatch[1];
            foundZipIndex = i;
            // Si la parte contenía más texto además del CP (ej: "28013 Madrid")
            const remaining = parts[i].replace(/\b\d{5}\b/, '').trim();
            if (remaining && !result.city) {
                result.city = remaining;
            }
            break;
        }
    }

    // Si encontramos provincia al final (último segmento si hay >= 3 partes)
    if (parts.length >= 3) {
        result.province = parts[parts.length - 1];
        if (!result.city) {
            result.city = parts[parts.length - 2];
        }
    } else if (parts.length === 2) {
        result.city = parts[1];
    }

    // Analizar la primera parte para calle y número
    const firstPart = parts[0];
    const streetNumberMatch = firstPart.match(/^(.*?)\s+(\d+[\w\s/-]*)$/);
    if (streetNumberMatch) {
        result.street = streetNumberMatch[1].trim();
        result.number = streetNumberMatch[2].trim();
    } else {
        result.street = firstPart;
        // Si el número estaba en el segundo segmento (ej: "Calle Bahía", "20", ...)
        if (parts.length > 1 && foundZipIndex !== 1 && /^\d+[\w\s/-]*$/.test(parts[1])) {
            result.number = parts[1];
        }
    }

    return result;
};

/**
 * Determina el nombre del cliente para el informe resumen:
 * - Si todas las órdenes seleccionadas pertenecen a un único cliente, devuelve ese cliente.
 * - Si se especificó un cliente en el filtro (fallback), devuelve ese cliente.
 * - Si hay órdenes de múltiples clientes o ninguna orden, devuelve 'Varios Clientes'.
 */
export function resolveReportClientName(
    orders: Array<{ clientName?: string | null }>,
    fallbackClientName?: string | null
): string {
    const uniqueClients = Array.from(
        new Set(orders.map(o => o.clientName?.trim()).filter((name): name is string => Boolean(name)))
    );
    if (uniqueClients.length === 1) {
        return uniqueClients[0];
    }
    if (fallbackClientName?.trim()) {
        return fallbackClientName.trim();
    }
    return 'Varios Clientes';
}
